import React, { useEffect, useMemo, useState } from "react";
import { Button, Input } from "reactstrap";

/**
 * Round 60 — client-side table pagination (UAT: "why can't we implement
 * pagination in tables"). The big MAFIA lists (Unreleased 561 rows,
 * Unchecked 1,560 rows, Import) previously rendered EVERY row in one
 * scroll area. This hook + footer paginate the already-filtered/sorted
 * client rows — no API or backend change, filters/sort/CSV keep operating
 * on the FULL list, only the rendered slice is paged.
 *
 * usePagedRows(rows, resetKey):
 *   - rows:     the filtered+sorted array to page
 *   - resetKey: any value that should snap back to page 1 when it changes
 *               (e.g. JSON of the active filters)
 *   returns { pagedRows, page, setPage, pageSize, setPageSize, pageCount,
 *             from, to, total }
 *
 * <TablePager {...pager} /> renders the footer bar (range text, page-size
 * select, First/Prev/Next/Last).
 */
export function usePagedRows(rows, resetKey) {
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);

  const total = rows ? rows.length : 0;
  const pageCount = Math.max(1, Math.ceil(total / pageSize));

  // Snap to page 1 whenever the underlying view changes (filter/sort/search
  // or a refresh that changed the row count).
  useEffect(() => {
    setPage(1);
    
  }, [resetKey, pageSize]);

  // Clamp if a refresh shrank the list below the current page.
  useEffect(() => {
    if (page > pageCount) setPage(pageCount);
  }, [page, pageCount]);

  const safePage = Math.min(page, pageCount);
  const pagedRows = useMemo(
    () => (rows || []).slice((safePage - 1) * pageSize, safePage * pageSize),
    [rows, safePage, pageSize]
  );

  const from = total === 0 ? 0 : (safePage - 1) * pageSize + 1;
  const to = Math.min(safePage * pageSize, total);

  return {
    pagedRows,
    page: safePage,
    setPage,
    pageSize,
    setPageSize,
    pageCount,
    from,
    to,
    total,
  };
}

const PAGE_SIZES = [25, 50, 100, 200];

const TablePager = ({
  page,
  setPage,
  pageSize,
  setPageSize,
  pageCount,
  from,
  to,
  total,
}) => {
  if (total <= PAGE_SIZES[0]) {
    // Tiny lists don't need chrome — just the count.
    return (
      <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 px-3 py-2 border-top bg-white">
        <span className="text-muted small">
          Showing {total.toLocaleString()} row{total === 1 ? "" : "s"}
        </span>
      </div>
    );
  }

  return (
    <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 px-3 py-2 border-top bg-white">
      <span className="text-muted small">
        Showing {from.toLocaleString()}–{to.toLocaleString()} of{" "}
        {total.toLocaleString()}
      </span>

      <div className="d-flex align-items-center gap-2 flex-wrap">
        <div className="d-flex align-items-center gap-1">
          <span className="text-muted small">Rows per page</span>
          <Input
            type="select"
            bsSize="sm"
            style={{ width: 78 }}
            value={pageSize}
            onChange={(e) => setPageSize(Number(e.target.value))}
          >
            {PAGE_SIZES.map((n) => (
              <option key={n} value={n}>
                {n}
              </option>
            ))}
          </Input>
        </div>

        <div className="btn-group">
          <Button
            color="light"
            size="sm"
            className="border"
            disabled={page <= 1}
            onClick={() => setPage(1)}
            title="First page"
          >
            <i className="ri-skip-back-mini-line" />
          </Button>
          <Button
            color="light"
            size="sm"
            className="border"
            disabled={page <= 1}
            onClick={() => setPage(page - 1)}
            title="Previous page"
          >
            <i className="ri-arrow-left-s-line" />
          </Button>
          <span
            className="d-inline-flex align-items-center px-2 border-top border-bottom small text-muted bg-light"
            style={{ minWidth: 92, justifyContent: "center" }}
          >
            Page {page} of {pageCount}
          </span>
          <Button
            color="light"
            size="sm"
            className="border"
            disabled={page >= pageCount}
            onClick={() => setPage(page + 1)}
            title="Next page"
          >
            <i className="ri-arrow-right-s-line" />
          </Button>
          <Button
            color="light"
            size="sm"
            className="border"
            disabled={page >= pageCount}
            onClick={() => setPage(pageCount)}
            title="Last page"
          >
            <i className="ri-skip-forward-mini-line" />
          </Button>
        </div>
      </div>
    </div>
  );
};

export default TablePager;
